import { useEffect } from 'react';

export type FullStoryGuidesUserInfo = {
  uid: string;
  properties: Record<string, string>;
};

declare global {
  interface Window {
    USETIFUL?: {
      user: {
        setId: (userId: string | null) => void;
        setTags: (tags: Record<string, string>) => void;
      };
      reinitialize: () => void;
    };
  }
}

function identifyFullStoryGuidesUser(userInfo: FullStoryGuidesUserInfo): () => void {
  const identifyGuidesUser = () => {
    try {
      window.USETIFUL?.user?.setId(userInfo.uid);
      window.USETIFUL?.user?.setTags?.(userInfo.properties);
      window.USETIFUL?.reinitialize();
    } catch {
      // The USETIFUL/FullStory Guides API can expose user.setId before the rest
      // of its API has initialized; reinitialize() then throws internally (e.g.
      // user.getProgress is not a function). Swallow it so a third-party init
      // race never escalates into an uncaught effect error that unmounts the page.
    }
  };

  if (window.USETIFUL?.user?.setId) {
    identifyGuidesUser();
    return () => undefined;
  }

  document.addEventListener('usetiful:fullApiReady', identifyGuidesUser);
  return () => document.removeEventListener('usetiful:fullApiReady', identifyGuidesUser);
}

export function useFullStoryGuidesIdentity(guideUserInfo: FullStoryGuidesUserInfo | null): void {
  const propertiesKey = guideUserInfo ? JSON.stringify(guideUserInfo.properties) : undefined;

  useEffect(() => {
    if (!guideUserInfo) {
      return;
    }

    return identifyFullStoryGuidesUser(guideUserInfo);
  }, [guideUserInfo?.uid, propertiesKey]);
}
